import axios from 'axios'
import {
  logoutAndClearStorage,
  refreshToken,
} from '../redux/keycloak/refreshToken'
import { store } from '../redux/store'
import { decodeRoles } from '../helpers/decode'
import i18n from '../i18n'
import keycloakConfig from '../keycloak'

const instanceAxios = axios.create({
  baseURL: process.env.REACT_APP_BASE_API_URL,
})

let failedQueue: any[] = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token)
    } else {
      prom.reject(error)
    }
  })
  failedQueue = []
}

const checkAndRefreshToken = async () => {
  const token = localStorage.getItem('token')
  if (!token) {
    logoutAndClearStorage(i18n.t('sessionAlerts.sessionExpired.title'))
    return null
  }

  const lastRefreshTime = parseInt(
    localStorage.getItem('lastRefreshTime') || '0'
  )
  const currentTime = Date.now()
  const timeElapsed = currentTime - lastRefreshTime

  if (timeElapsed >= 25 * 60 * 1000) {
    try {
      const newAccessToken = await refreshToken()
      if (newAccessToken) {
        localStorage.setItem('token', newAccessToken)
        localStorage.setItem('lastRefreshTime', currentTime.toString())
        const clientId = localStorage.getItem('tenantClientId') || keycloakConfig.clientId
        const roles = decodeRoles(newAccessToken, clientId)
        if (!roles) {
          console.warn('User access not authorized')
          logoutAndClearStorage(i18n.t('sessionAlerts.sessionExpired.title'))
          return null
        }
        localStorage.setItem('roles', JSON.stringify(roles))
        store.dispatch({ type: 'auth/updateRoles', payload: roles })
        return newAccessToken
      }
    } catch (error) {
      console.error('Error refreshing token:', error)
      logoutAndClearStorage(i18n.t('sessionAlerts.sessionExpired.title'))
      return null
    }
  }

  return token
}

instanceAxios.interceptors.request.use(
  async (config) => {
    const token = await checkAndRefreshToken()
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

instanceAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    if (error.config.url.includes('/token')) {
      logoutAndClearStorage(i18n.t('sessionAlerts.sessionExpired.title'))
      return
    }
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        const newAccessToken = await refreshToken()
        if (newAccessToken) {
          localStorage.setItem('token', newAccessToken)
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`
          return instanceAxios(originalRequest)
        }
      } catch (err) {
        console.error('Session expired. Redirecting to login.')
        logoutAndClearStorage(i18n.t('sessionAlerts.sessionExpired.title'))
        return
      }
    }
    return Promise.reject(error)
  }
)

export default instanceAxios
